import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useTranslate } from "utils/i18n";
import { figmaTokens } from "assets/theme/designTokens";

export default function DashboardMapWidget({ areaAttendance, loading = false, showOpenButton = true }) {
  const { t } = useTranslate();
  const assigned = Number(areaAttendance?.teachersAssigned) || 0;
  const checked = Number(areaAttendance?.teachersChecked) || 0;
  const rate = Number(areaAttendance?.checkInRate) || 0;

  return (
    <Card
      sx={{
        borderRadius: "16px",
        border: `1px solid ${figmaTokens.borderSubtle}`,
        boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
      <SoftBox sx={{ p: 2.25 }}>
        <SoftBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 2 }}>
          <SoftBox>
            <SoftTypography variant="overline" sx={{ fontWeight: 800, color: figmaTokens.textMuted, letterSpacing: "0.12em" }}>
              {t("map.widget.kicker", "GEO DISTRIBUTION")}
            </SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
              {t("map.widget.title", "Teacher map")}
            </SoftTypography>
          </SoftBox>
          <Icon sx={{ color: figmaTokens.accent, fontSize: 28 }}>map</Icon>
        </SoftBox>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: t("map.panel.assigned", "Assigned"), value: loading ? "…" : assigned },
            { label: t("map.panel.checked", "Checked"), value: loading ? "…" : checked },
            { label: t("map.kpi.check_in_rate", "Check-in rate"), value: loading ? "…" : `${rate}%` },
          ].map((item) => (
            <Grid item xs={4} key={item.label}>
              <SoftBox
                sx={{
                  p: 1.25,
                  borderRadius: "12px",
                  bgcolor: figmaTokens.pageBg,
                  border: `1px solid ${figmaTokens.borderSubtle}`,
                  textAlign: "center",
                }}
              >
                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, display: "block" }}>
                  {item.label}
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
                  {item.value}
                </SoftTypography>
              </SoftBox>
            </Grid>
          ))}
        </Grid>

        <SoftTypography variant="caption" sx={{ color: figmaTokens.textSecondary, display: "block", mb: 2 }}>
          {t(
            "map.widget.hint",
            "Explore teacher check-ins on the map with geo filters, radius, heatmap, and export."
          )}
        </SoftTypography>

        {showOpenButton && (
          <Button
            component={Link}
            to="/carte-enseignants"
            variant="contained"
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              py: 1,
              color: "#fff",
              background: figmaTokens.gradientCSS,
              boxShadow: "0 6px 18px rgba(102, 126, 234, 0.35)",
            }}
          >
            {t("map.widget.open", "Open teacher distribution map")}
          </Button>
        )}
      </SoftBox>
    </Card>
  );
}

DashboardMapWidget.propTypes = {
  areaAttendance: PropTypes.shape({
    teachersAssigned: PropTypes.number,
    teachersChecked: PropTypes.number,
    checkInRate: PropTypes.number,
  }),
  loading: PropTypes.bool,
  showOpenButton: PropTypes.bool,
};

DashboardMapWidget.defaultProps = {
  areaAttendance: null,
  loading: false,
  showOpenButton: true,
};
